import * as React from 'react';

import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import Dropdown from 'af-components/Controls/Dropdown';

import CustomPicker from './CustomPicker';
import DailyPicker from './DailyPicker';
import FiscalPicker from './FiscalPicker';
import MonthlyPicker from './MonthlyPicker';
import styles from './styles.module.scss';
import WeeklyPicker from './WeeklyPicker';

interface DateFilterOption {
	id: TimePeriodRecurrence;
	label: string;
}

const PERIOD_OPTIONS = [
	{ id: TimePeriodRecurrence.DAILY, label: 'Daily' },
	{ id: TimePeriodRecurrence.WEEKLY, label: 'Weekly' },
	{ id: TimePeriodRecurrence.MONTHLY, label: 'Monthly' },
	{ id: TimePeriodRecurrence.FISCAL_YEAR, label: 'Fiscal Year' },
	{ id: TimePeriodRecurrence.LAST_6_MONTHS, label: 'Last 6 Months' },
	{ id: TimePeriodRecurrence.YEAR_TO_DATE, label: 'Year to Date' },
	{ id: TimePeriodRecurrence.CUSTOM, label: 'Custom' },
];

type Props = {
	endDate: Date;
	maxCustomDays?: number;
	options?: DateFilterOption[];
	changePeriod: (id: TimePeriodRecurrence, selected: Date) => void;
	onChange: (start: Date, end: Date) => void;
	period: TimePeriodRecurrence;
	startDate: Date;
};

const DateFilter: React.FC<Props> = (props) => {

	const {
		options = PERIOD_OPTIONS,
		onChange,
		changePeriod,
		endDate,
		maxCustomDays,
		period,
		startDate,
	} = props;

	const [selected, setSelected] = React.useState<Date>(startDate);

	React.useEffect(() => {
		setSelected(startDate);
	}, [startDate]);

	const onValueChange = React.useCallback((option: DateFilterOption) => {
		changePeriod(option.id, selected);
	}, [changePeriod, selected]);

	const renderOption = React.useCallback((option: DateFilterOption) => {
		return (
			<div >
				{option.label}
			</div>
		);
	}, []);

	const renderDatePicker = React.useCallback(() => {
		return (
			<div className={styles['date-period']}>
				<Dropdown<DateFilterOption>
					defaultValue={options.find((o) => o.id === period) ?? options[0]}
					onValueChange={onValueChange}
					options={options}
					renderMenuItem={renderOption}
					valueKey={'id'}
					withCaret={true}
				/>
			</div>
		);
	}, [onValueChange, options, period, renderOption]);

	const selectDay = React.useCallback((date: Date) => {
		onChange(date, date);
		setSelected(date);
	}, [onChange]);

	const selectPeriod = React.useCallback((_selected: Date, start: Date, end: Date) => {
		onChange(start, end);
		setSelected(_selected);
	}, [onChange]);

	const renderPeriodPicker = React.useCallback(() => {
		switch (period) {
			case TimePeriodRecurrence.DAILY:
				return (
					<DailyPicker
						onChange={selectDay}
						selected={selected}
					/>
				);
			case TimePeriodRecurrence.WEEKLY:
				return (
					<WeeklyPicker
						onChange={selectPeriod}
						selected={selected}
					/>
				);
			case TimePeriodRecurrence.MONTHLY:
				return (
					<MonthlyPicker
						onChange={selectPeriod}
						selected={selected}
					/>
				);
			case TimePeriodRecurrence.CUSTOM:
				return (
					<CustomPicker
						end={endDate}
						maxDays={maxCustomDays}
						onChange={selectPeriod}
						start={selected}
					/>
				);
			case TimePeriodRecurrence.FISCAL_YEAR:
				return (
					<FiscalPicker
						onChange={selectPeriod}
						selected={selected}
					/>
				);
			case TimePeriodRecurrence.LAST_6_MONTHS:
			case TimePeriodRecurrence.YEAR_TO_DATE:
			default:
				return null;
		}
	}, [endDate, maxCustomDays, period, selectDay, selectPeriod, selected]);

	return <div className={styles['date-filter']}>

		{renderDatePicker()}
		{renderPeriodPicker()}
	</div>;
};

export default React.memo(DateFilter);
